import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, FileText, Sparkles, Loader2 } from 'lucide-react';
import { API, useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { CHAT } from '@/constants/testIds';
import { toast } from 'sonner';

const CATEGORY_META = {
  'hr': { name: 'HR', hint: 'Ask about benefits, hiring, compensation or the employee handbook.' },
  'operations': { name: 'Operations', hint: 'Ask about internal procedures, workflows or back-office playbooks.' },
  'compliance': { name: 'Compliance', hint: 'Ask about regulatory guidelines, audit standards, KYC/AML procedures or firm policy.' },
  'client-services': { name: 'Client Services', hint: 'Ask about onboarding, service standards or account operations.' },
  'wealth-advisors': { name: 'Wealth Advisors', hint: 'Ask about portfolio strategy, planning frameworks or best practices.' },
  'alternative-solutions': { name: 'Alternative Solutions', hint: 'Ask about private equity, hedge funds, structured products or alts research.' },
};

export default function ChatPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const meta = CATEGORY_META[categoryId];

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!meta) navigate('/');
  }, [meta, navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  if (!meta) return null;

  const send = async (e) => {
    e?.preventDefault?.();
    const question = input.trim();
    if (!question || sending) return;
    setInput('');
    const userMsg = { role: 'user', content: question };
    // Add user + placeholder assistant message that will be filled in
    setMessages((prev) => [
      ...prev,
      userMsg,
      { role: 'assistant', content: '', sources: [], streaming: true },
    ]);
    setSending(true);

    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ category: categoryId, question, top_k: 4 }),
      });
      if (!resp.ok || !resp.body) {
        let detail = 'Failed to reach the assistant.';
        try {
          const err = await resp.json();
          if (typeof err?.detail === 'string') detail = err.detail;
        } catch (_) {}
        throw new Error(detail);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const evt of events) {
          const line = evt.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let msg;
          try {
            msg = JSON.parse(payload);
          } catch {
            continue;
          }
          if (msg.type === 'sources') {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = { ...last, sources: msg.sources || [] };
              }
              return copy;
            });
          } else if (msg.type === 'token') {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = {
                  ...last,
                  content: (last.content || '') + (msg.content || ''),
                };
              }
              return copy;
            });
          } else if (msg.type === 'error') {
            toast.error(msg.message || 'Streaming error');
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = {
                  ...last,
                  content: (last.content || '') + `\n\n⚠ ${msg.message || 'Streaming error'}`,
                  error: true,
                };
              }
              return copy;
            });
          } else if (msg.type === 'done') {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = { ...last, streaming: false };
              }
              return copy;
            });
          }
        }
      }
    } catch (err) {
      const message = err?.message || 'Failed to reach the assistant.';
      toast.error(message);
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') {
          copy[copy.length - 1] = {
            ...last,
            content: (last.content || '') + `\n\n⚠ ${message}`,
            streaming: false,
            error: true,
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div data-testid={CHAT.page} className="flex flex-col h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white flex-shrink-0">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/" data-testid={CHAT.backBtn} className="flex items-center gap-3">
            <Logo variant="mark" className="h-8 w-8 rounded-md" alt="STPK" />
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-[#0A2540] gap-2 px-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to categories</span>
            </Button>
          </Link>
          <div className="h-6 w-px bg-slate-200" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
              Category Chat
            </div>
            <h1
              data-testid={CHAT.categoryTitle}
              className="font-display text-lg text-[#0A2540] leading-tight"
            >
              {meta.name}
            </h1>
          </div>
          <div className="ml-auto hidden md:flex items-center gap-2 text-xs text-slate-400">
            <div className="h-2 w-2 rounded-full bg-green-500" />
            Scoped to {meta.name} sources only
          </div>
          <ThemeToggle className="ml-auto md:ml-0" />
        </div>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid={CHAT.messagesList}
        className="flex-1 overflow-y-auto stpk-scroll"
      >
        <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
          {messages.length === 0 && (
            <div className="text-center py-20">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white mb-6">
                <Sparkles className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h2 className="font-display text-3xl text-[#0A2540]">
                {meta.name} assistant
              </h2>
              <p className="mt-3 text-sm text-slate-500 max-w-md mx-auto leading-relaxed">
                {meta.hint} Every answer is retrieved exclusively from documents
                tagged to this category.
              </p>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble key={i} message={m} />
          ))}

          {sending && messages.length > 0 && !messages[messages.length - 1]?.content && (
            <div className="text-xs text-slate-400 flex items-center gap-2 pl-11">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching {meta.name} knowledge…
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 bg-white flex-shrink-0">
        <form onSubmit={send} className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-end gap-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={`Ask about ${meta.name.toLowerCase()}…`}
              data-testid={CHAT.input}
              rows={1}
              className="resize-none min-h-[44px] max-h-32 rounded-md border-slate-300 focus-visible:ring-[#0A2540]"
            />
            <Button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid={CHAT.sendBtn}
              className="h-11 bg-[#0A2540] hover:bg-slate-800 text-white rounded-md px-5 gap-2"
            >
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline">Send</span>
            </Button>
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-slate-400">
            Enter to send · Shift+Enter for newline · Signed in as {user?.email}
          </div>
        </form>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div
          data-testid={CHAT.userMessage}
          className="max-w-2xl bg-[#0A2540] text-white rounded-lg px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
        >
          {message.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div className="h-8 w-8 rounded-md border border-slate-200 bg-white flex items-center justify-center flex-shrink-0">
        <Sparkles className="h-4 w-4 text-[#D4AF37]" />
      </div>
      <div
        data-testid={CHAT.assistantMessage}
        className={`max-w-2xl flex-1 bg-white border rounded-lg px-4 py-3 ${
          message.error ? 'border-red-200' : 'border-slate-200'
        }`}
      >
        <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
          {message.content}
          {message.streaming && (
            <span className="inline-block w-1.5 h-4 ml-0.5 bg-[#0A2540] align-middle animate-pulse" />
          )}
        </div>
        {message.sources && message.sources.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">
              Sources
            </div>
            <div className="flex flex-wrap gap-2">
              {message.sources.map((s, idx) => (
                <div
                  key={idx}
                  data-testid={CHAT.source}
                  title={s.snippet}
                  className="inline-flex items-center gap-1.5 text-xs bg-slate-50 border border-slate-200 text-slate-700 rounded-md px-2.5 py-1"
                >
                  <FileText className="h-3 w-3 text-[#0A2540]" />
                  <span className="font-medium">{s.title}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-400 font-mono text-[10px]">
                    {(s.score * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
